import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  useMediaQuery,
  useTheme,
  Tooltip as MuiTooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  TrendingUp as UpIcon,
  TrendingDown as DownIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Article as PostIcon,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import { useAuth } from '../contexts/AuthContext';
import { SectionCard } from './SectionCard';
import { SectionSkeleton } from './SectionSkeleton';
import { getPlatformColor, getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

interface DayPost {
  id: string;
  excerpt: string;
}

interface SeriesPoint {
  date: string;
  followers: number | null;
  /** Change against the previous *recorded* day. null on the first day we ever saw the account. */
  delta: number | null;
  /**
   * Gross follows and unfollows, where the platform reports them — Facebook and Instagram do,
   * LinkedIn and X do not. `delta` is the net of the two and hides most of the activity: 9
   * follows against 23 unfollows reads as -14.
   */
  follows: number | null;
  unfollows: number | null;
  /** Of `follows`, how many came from ads. LinkedIn reports this split; null elsewhere. */
  paid_follows?: number | null;
  /**
   * Account-level reach, engaged accounts and profile views for the day.
   *
   * **The only figures here that include paid delivery.** Per-post engagement comes from the
   * media insights, which report organic activity only — so a boosted post shows 3 likes
   * while the Instagram app shows 105. Instagram only; null on every other platform.
   */
  reach: number | null;
  accounts_engaged: number | null;
  profile_views: number | null;
  /** What went out on this platform that day. Empty on most days. */
  posts: DayPost[];
}

interface HistoryAccount {
  platform: string;
  account_ref: string;
  account: string;
  series: SeriesPoint[];
  net_change: number;
  best_day: SeriesPoint | null;
  worst_day: SeriesPoint | null;
}

const formatDay = (d: string) =>
  new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

/**
 * Follower history per account, with what was published alongside it.
 *
 * Separate from AudienceTotals, which answers "where do we stand today". This answers "what
 * happened, and when" — the question a single current number cannot.
 *
 * Every account gets its own chart rather than one combined series: the accounts differ by
 * three orders of magnitude (a page with 0 followers next to one with 2,000), so a shared axis
 * would flatten everything small into the baseline.
 */
export const FollowerHistory: React.FC<{ days?: number }> = ({ days = 90 }) => {
  const { token } = useAuth();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [accounts, setAccounts] = useState<HistoryAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [platform, setPlatform] = useState<string>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    // `token` is null on the first render, while AuthContext is still restoring the session
    // from storage. Fetching then sends `Bearer null`, and AuthContext's 401 interceptor
    // treats any unauthenticated API response as a dead session and signs the user out —
    // so an unguarded fetch here can log someone out just for opening the page.
    if (!token) return;
    // Spinner only on the very first load. A re-fetch — after hiding an account, say — would
    // otherwise swap a populated panel for a spinner and read as the content disappearing.
    //
    // Read through a ref rather than depending on `accounts`: making it a dependency of this
    // callback recreates it on every fetch, which re-runs the effect below and loops.
    if (!loadedOnce.current) setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/audience/history?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not load follower history');
      const data = await res.json();
      setAccounts(data.accounts || []);
      loadedOnce.current = true;
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load follower history');
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  // Re-fetch when an account is hidden or unhidden anywhere on the page. Same server-side
  // list drives every audience panel, so they must not drift apart until a reload.
  useEffect(() => {
    const onHiddenChanged = () => { load(); };
    window.addEventListener('shoutvector:audience-hidden-changed', onHiddenChanged);
    return () => window.removeEventListener('shoutvector:audience-hidden-changed', onHiddenChanged);
  }, [load]);


  const platforms = useMemo(
    () => Array.from(new Set(accounts.map(a => a.platform))).sort(),
    [accounts]
  );

  const shown = platform === 'all' ? accounts : accounts.filter(a => a.platform === platform);

  if (loading) {
    return <SectionSkeleton frameless chart />;
  }
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  if (!accounts.length) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No follower history yet. It builds up as the hourly refresh records each account —
        a chart needs at least two days of readings.
      </Alert>
    );
  }

  return (
    <SectionCard
      frameless
      title="Follower History"
      actions={platforms.length > 1 && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={platform}
          onChange={(_, v) => v && setPlatform(v)}
          sx={{ flexWrap: 'wrap' }}
        >
          <ToggleButton value="all">All</ToggleButton>
          {platforms.map(p => (
            <ToggleButton key={p} value={p}>{getPlatformName(p)}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    >

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
        {shown.map(acc => {
          const key = `${acc.platform}:${acc.account_ref}`;
          const colour = getPlatformColor(acc.platform);
          const points = acc.series.filter(p => p.followers !== null);
          // A single reading draws no line, and a flat one-point chart reads as broken.
          const plottable = points.length >= 2;
          const movement = acc.series.filter(p => p.delta !== null && p.delta !== 0).reverse();

          return (
            <Card key={key} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip
                    size="small"
                    label={getPlatformName(acc.platform)}
                    sx={{ bgcolor: colour, color: '#fff' }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {acc.account || acc.account_ref}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="h6">
                    {points.length ? points[points.length - 1].followers?.toLocaleString() : '—'}
                  </Typography>
                  {acc.net_change !== 0 && (
                    <Chip
                      size="small"
                      icon={acc.net_change > 0 ? <UpIcon /> : <DownIcon />}
                      label={signed(acc.net_change)}
                      color={acc.net_change > 0 ? 'success' : 'error'}
                      variant="outlined"
                    />
                  )}
                </Box>

                {plottable ? (
                  <Box sx={{ height: 180 }}>
                    <Line
                      data={{
                        labels: points.map(p => formatDay(p.date)),
                        datasets: [{
                          label: 'Followers',
                          data: points.map(p => p.followers as number),
                          borderColor: colour,
                          backgroundColor: colour + '22',
                          fill: true,
                          tension: 0.3,
                          pointRadius: points.map(p => (p.posts.length ? 4 : 0)),
                          pointBackgroundColor: colour,
                        }],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            callbacks: {
                              // The whole point of the chart: a day's movement next to what
                              // went out that day.
                              afterBody: (items) => {
                                const p = points[items[0].dataIndex];
                                const lines: string[] = [];
                                if (p.delta !== null) lines.push(`Change: ${signed(p.delta)}`);
                                for (const post of p.posts) {
                                  lines.push(`Posted: ${post.excerpt.slice(0, 60)}…`);
                                }
                                return lines;
                              },
                            },
                          },
                        },
                        scales: { y: { beginAtZero: false } },
                      }}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Only one day of readings so far — a line needs two.
                  </Typography>
                )}

                {/* Account reach is the one figure that counts paid delivery, so it is the
                    only honest answer to "how many people actually saw our posts". Summed
                    rather than averaged: the question is total exposure over the window. */}
                {(() => {
                  const measured = acc.series.filter(p => typeof p.reach === 'number');
                  if (measured.length === 0) return null;
                  const reach = measured.reduce((n, p) => n + (p.reach || 0), 0);
                  const engaged = measured.reduce((n, p) => n + (p.accounts_engaged || 0), 0);
                  return (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {reach.toLocaleString()} accounts reached
                        {engaged > 0 && ` · ${engaged.toLocaleString()} engaged`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Account-wide over {measured.length} measured day
                        {measured.length === 1 ? '' : 's'}, and the only figure here that counts
                        boosted delivery. Per-post engagement is organic only, so it reads lower.
                      </Typography>
                    </Box>
                  );
                })()}

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {movement.length
                      ? `${movement.length} day${movement.length === 1 ? '' : 's'} with movement`
                      : 'No change in this period'}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {movement.length > 0 && (
                    <IconButton size="small" onClick={() => setExpanded(expanded === key ? null : key)}>
                      {expanded === key ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                  )}
                </Box>

                <Collapse in={expanded === key}>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ mt: 1, minWidth: narrow ? 0 : 520 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Day</TableCell>
                        <TableCell align="right">Followers</TableCell>
                        <TableCell align="right">Gained</TableCell>
                        <TableCell align="right">Lost</TableCell>
                        <TableCell align="right">Net</TableCell>
                        {!narrow && <TableCell>Posted that day</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {movement.map(p => (
                        <TableRow key={p.date}>
                          <TableCell>{formatDay(p.date)}</TableCell>
                          <TableCell align="right">{p.followers?.toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>
                            {p.follows === null ? '—' : p.follows}
                            {(p.paid_follows || 0) > 0 && (
                              <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {' '}({p.paid_follows} from ads)
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            {p.unfollows === null ? '—' : p.unfollows}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: (p.delta as number) > 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
                          >
                            {signed(p.delta as number)}
                          </TableCell>
                          {!narrow && (
                          <TableCell>
                            {p.posts.length ? (
                              <MuiTooltip title={p.posts.map(x => x.excerpt).join('\n\n')}>
                                <Chip
                                  size="small"
                                  icon={<PostIcon />}
                                  label={p.posts.length === 1 ? '1 post' : `${p.posts.length} posts`}
                                  variant="outlined"
                                />
                              </MuiTooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </TableContainer>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        A post on the same day as a change is <strong>not proof it caused it</strong>. Follower
        counts move for many reasons, and a platform removing fake accounts will dwarf anything a
        single post does — the sharp Instagram drop in September was exactly that. Treat this as
        somewhere to look, not an answer.
      </Alert>
    </SectionCard>
  );
};

export default FollowerHistory;
